import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'
import { updateCampaignQuoteTotal } from './campaignQuote'

const CREATE_REQUEST = 'realhub/campaignQuoteSection/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/campaignQuoteSection/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/campaignQuoteSection/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/campaignQuoteSection/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/campaignQuoteSection/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/campaignQuoteSection/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/campaignQuoteSection/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/campaignQuoteSection/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/campaignQuoteSection/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function createCampaignQuoteSection(campaignQuoteSection, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      campaign_quote_section: campaignQuoteSection,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const { campaign_quote_id } = campaignQuoteSection
    const promise = api(`/agency/campaign_quotes/${campaign_quote_id}/sections.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateCampaignQuoteSection(campaignQuoteSection, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      campaign_quote_section: campaignQuoteSection,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { campaign_quote_id, id } = campaignQuoteSection
    const promise = api(`/agency/campaign_quotes/${campaign_quote_id}/sections/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

export function updateQuoteSectionSortOrder(campaignQuote, quoteSectionIds, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      sort_order: quoteSectionIds,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { id } = campaignQuote
    const promise = api(`/agency/campaign_quotes/${id}/sections/update_sort_order.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.CAMPAIGN_QUOTE_SECTION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

export function deleteCampaignQuoteSection(campaignQuoteSection, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const { campaign_quote_id, id } = campaignQuoteSection
    const promise = api(`/agency/campaign_quotes/${campaign_quote_id}/sections/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Update Quote Total
        if (json.quote_total){
          dispatch(updateCampaignQuoteTotal(json.campaign_quote_id, json.quote_total))
        }

        const normalizedJson = normalize(json, Schemas.CAMPAIGN_QUOTE_SECTION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return { ...state, deleting: true }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
